import { autoinject } from 'aurelia-framework';

import { SearchFieldModel } from '~/components/field/common/search-definition';
import { CommonURLParam } from '~/constants';
import { ClassicFormStateModelBackend } from '~/models/classic-form-state-model';
import { FieldModelMap, FormModel } from '~/models/form-model';
import { ValueMap } from '~/models/value-map';
import FormModelUtils from '~/utils/form-model-utils';
import { Client, IFrame } from '@stomp/stompjs';
import { Logger } from '~/utils/logger';

export enum ParamType {
	FORM = 'form',
	CUSTOMISATION = 'customisation',
	DIVISION = 'division',
	BATCH_ID = 'batch-id',
}

export interface APIParameters {
	[key: string]: string | number | any[] | { [key: string]: any } | undefined;
}

/**
 * DownloadSpec is a simple object that contains the URL and filename of a file to download.
 */
export interface DownloadSpec {
	/**
	 * URL to download the file from.
	 */
	url: string;
	/**
	 * Filename to save the file as.
	 */
	filename: string;
}

@autoinject
export class GenericApi {
	/**
	 * formName is a global variable that is automatically applied to fetch() calls for security.
	 */
	public static formName: string = '';

	/**
	 * customisationName is a global variable that is automatically applied to fetch() calls when set so that
	 * relevant customisation information is retrieved from an endpoint.
	 */
	public static customisationName: string;

	/**
	 * batchId is a global variable that is automattically applied to fetch() calls to indicated if the current
	 * request is under a receipt processing context;
	 */
	public static batchId?: string;

	private static readonly apiServerHostnameAndPort =
		GenericApi.computeApiServerHostnameAndPort();

	/**
	 * @deprecated NOTE(Jae): 2020-04-07: This should not be utilized anymore. FetchUtils fetch functions will automatically prepend this to URLs.
	 * If you do need to build a URL yourself for an edge-case reason, use FetchUtils.getBaseUrl(). The aim will be to move
	 * this compute logic elsewhere / inline where we call FetchUtils.setBaseUrl.
	 */
	public static readonly apiServerURL =
		window.location.protocol +
		'//' +
		GenericApi.apiServerHostnameAndPort +
		'/';

	public static readonly webSocketApiServerURL =
		(window.location.protocol === 'https:' ? 'wss:' : 'ws:') +
		'//' +
		GenericApi.apiServerHostnameAndPort +
		'/';

	/**
	 * Post process JSON in the style of JSON.parse(jsonText, reviver)
	 * @param jsonData
	 * @param reviver
	 */
	protected static postProcessJSON(jsonData, reviver: (key, value) => void) {
		const recursivelyProcess = (key, data) => {
			if (data != null && typeof data === 'object') {
				const properties = Object.getOwnPropertyNames(data);
				for (const p of properties) {
					if (!(data.constructor === Array && p === 'length')) {
						recursivelyProcess(p, data[p]);
					}
				}
			}
			reviver(key, data);
		};
		recursivelyProcess('', jsonData);
		return jsonData;
	}

	public static getDivision(): string | undefined {
		// NOTE(Jae): 2020-04-01
		// In the future I think I want this to belong in a "DivisionState" class that
		// is dedicated to updating/getting this value for you. For now, its fine here.
		const urlParams = new URLSearchParams(
			window.location.hash.split('?')[1],
		);
		const division = urlParams.get(CommonURLParam.DIVISION);
		if (division) {
			return division;
		}
		return undefined;
	}

	/**
	 * extract batch id from current route
	 * @returns batchId if batch-id param is present in the url. Otherwise undefined
	 */
	public static getBatchId(): string | undefined {
		const urlParams = new URLSearchParams(
			window.location.hash.split('?')[1],
		);
		const batchId = urlParams.get(CommonURLParam.BATCH_ID);
		if (batchId) {
			return batchId;
		}
		return undefined;
	}

	/**
	 * Get FieldModel from JSON and populate toString and valueOf methods
	 *
	 * @deprecated translateFormModelFormat should just be called directly instead
	 * of using this transformation. Applying a toString() / valueOf() prototype manually
	 * to each object is not a good idea and we should avoid relying on that behaviour anywhere.
	 */
	public static getFieldModel(data): Promise<any> {
		const getValueFunc = function () {
			return this.value;
		};
		const toStringFunc = function () {
			if (this.value) {
				return this.value.toString();
			}
			return '';
		};

		const fieldModel = GenericApi.translateFormModelFormat(data);
		return GenericApi.postProcessJSON(fieldModel, (key, value) => {
			if (value && value.name) {
				value.valueOf = getValueFunc;
				value.toString = toStringFunc;
			}
			return value;
		});
	}

	/**
	 * Same as getFieldModel above, but works over a list of data instead of just one data element.
	 */
	protected static getFieldModelList(data): Promise<FieldModelMap[]> {
		return GenericApi.getFieldOrFormModelList(data, false) as Promise<
			FieldModelMap[]
		>;
	}

	protected static getFormModelList(data): Promise<FormModel[]> {
		return GenericApi.getFieldOrFormModelList(data, true) as Promise<
			FormModel[]
		>;
	}

	private static getFieldOrFormModelList(
		data,
		formModelFormat: boolean,
	): Promise<FieldModelMap[] | FormModel[]> {
		const getValueFunc = function () {
			return this.value;
		};
		const toStringFunc = function () {
			if (this.value) {
				return this.value.toString();
			}
			return '';
		};
		const fieldModelMapList = GenericApi.translateFormModelListFormat(
			data,
			formModelFormat,
		);

		return GenericApi.postProcessJSON(fieldModelMapList, (key, value) => {
			if (value && value.name) {
				value.valueOf = getValueFunc;
				value.toString = toStringFunc;
			}
			return value;
		});
	}

	private static translateFormModelListFormat(
		data: any,
		formModelFormat: boolean,
	): FieldModelMap[] | FormModel[] {
		return data.map((row) => {
			if (formModelFormat) {
				return GenericApi.strictTranslateFormModelFormat(row);
			} else {
				return GenericApi.strictTranslateFormModelFormat(row).fields;
			}
		});
	}

	public static translateFormModelToValueMap(formModel: FormModel) {
		const valueMap: ValueMap = { values: {} };
		Object.keys(formModel.fields)
			.filter((fieldName) => formModel.fields[fieldName] !== undefined)
			.forEach((fieldName) => {
				valueMap.values[fieldName] = formModel.fields[fieldName].value;
			});

		if (formModel.formModelList) {
			valueMap.valuesList = [];
			formModel.formModelList.forEach((childFormModel) => {
				const childValueMap =
					this.translateFormModelToValueMap(childFormModel);
				valueMap.valuesList.push(childValueMap);
			});
		}

		return valueMap;
	}

	public static translateFormModelToClassicFormStateModel(
		formModel: FormModel,
	) {
		const classicFormStateModel: ClassicFormStateModelBackend = {
			values: {},
			fieldModel: {},
		};

		classicFormStateModel.fieldModel = FormModelUtils.cloneModel(
			formModel.fields,
		);

		const validFieldNames = Object.keys(formModel.fields).filter(
			(fieldName) => formModel.fields[fieldName] !== undefined,
		);

		validFieldNames.forEach((fieldName) => {
			delete classicFormStateModel.fieldModel[fieldName].value;
			classicFormStateModel.values[fieldName] =
				formModel.fields[fieldName].value;
		});

		if (formModel.formModelList) {
			classicFormStateModel.formModelList = [];
			formModel.formModelList.forEach((childFormModel) => {
				classicFormStateModel.formModelList.push(
					this.translateFormModelToClassicFormStateModel(
						childFormModel,
					),
				);
			});
		}

		return classicFormStateModel;
	}

	/**
	 * translateFormModelFormat exists because historically the backend endpoints would return
	 * the value within the fieldModel. This method makes the structure of our fieldModels
	 * to include the value so no front-end code needed to change.
	 */
	public static translateFormModelFormat(formModel: any): FormModel | any {
		if (
			formModel === undefined ||
			formModel.values === undefined ||
			formModel.fieldModel === undefined
		) {
			// Not a form model.
			return formModel;
		}

		return GenericApi.strictTranslateFormModelFormat(formModel);
	}

	public static strictTranslateFormModelFormat(formModel: any): FormModel {
		if (
			formModel.values === undefined ||
			formModel.fieldModel === undefined
		) {
			// Not a form model.
			throw new Error(
				'Unexpected response body format. Missing "values" and/or "fieldModel" property.',
			);
		}

		formModel.fields = formModel.fieldModel;
		Object.keys(formModel.values).forEach((fieldName) => {
			if (formModel.fields[fieldName]) {
				formModel.fields[fieldName].value = formModel.values[fieldName];
			}
		});
		delete formModel.values;
		delete formModel.fieldModel;
		GenericApi.applySearchFieldModel(formModel);

		// Form model may contain one/more lists of child form models
		// that should also be translated into correct format
		Object.keys(formModel).forEach((modelList) => {
			const formModelList = formModel[modelList];
			if (formModelList && Array.isArray(formModelList)) {
				formModelList.map((row) =>
					GenericApi.translateFormModelFormat(row),
				);
			}
		});

		return formModel;
	}

	/**
	 * applySearchFieldModel used to exist on ClassicForm.formModelChanged() but we
	 * needed this logic to work in <classic-panel> contexts as well, so the logic was moved
	 * so that these filterBy rules are applied to *any* formModel in any context.
	 */
	private static applySearchFieldModel(formModel: FormModel): void {
		const tableCodes: string[] = [];
		Object.keys(formModel.fields).forEach((fieldName) => {
			if (fieldName.length > 2) {
				const tableCode = Object.keys(formModel.fields)[0].substr(0, 2);
				if (tableCodes.indexOf(tableCode) === -1) {
					tableCodes.push(tableCode);
				}
			}
		});
		tableCodes.forEach((tableCode) => {
			if (formModel.fields[tableCode + 'z_Fund']) {
				let filterByFundModel: SearchFieldModel = null;
				if (formModel.fields[tableCode + 'z_Member']) {
					filterByFundModel =
						formModel.fields[tableCode + 'z_Member'];
					filterByFundModel.filterBy = [tableCode + 'z_Fund'];
				}
				if (formModel.fields[tableCode + 'z_Payroll']) {
					filterByFundModel =
						formModel.fields[tableCode + 'z_Payroll'];
					filterByFundModel.filterBy = [tableCode + 'z_Fund'];
				}
			}
		});
	}

	/**
	 * Get JSON data from a response, throwing errors when necessary
	 * 1. Throw reject promise with JSON error data from failed http request
	 * 2. Otherwise if the error response contains 'statusText' return an object with 'error' as statusText
	 * 3. Otherwise return a promise containing the JSON data
	 * @param data
	 */
	public static getJSON(data, preserveStatus?: boolean): Promise<any> {
		if (data instanceof Response) {
			const hasJSONHeaders =
				data.headers.get('Content-Type') &&
				data.headers.get('Content-Type').includes('application/json');
			if (!data.ok) {
				if (hasJSONHeaders) {
					return data.json().then(Promise.reject);
				} else if (data.statusText) {
					(data as any).error = data.statusText;
					throw new Error(data.statusText);
				}
				// NOTE: Jake: 2019-10-18 - WEBUI-2595
				// This case throws if the server is completely offline.
				// We can probably improve this to read the "status" code
				// and give a better message like "Cannot connect to Acurity"
				// if its a 500 or something.
				const error = new Error('Failed to process JSON response.');
				// Preserving the status on the error object ensures 500 responses
				// can be caught to prevent infinite login loops
				if (preserveStatus) {
					(error as any).status = data.status;
				}
				throw error;
			} else {
				if (hasJSONHeaders) {
					return data.json();
				} else {
					return data as any;
				}
			}
		} else {
			// NOTE: Jake: 2019-01-03 - WEBUI-1148
			// If using raw data and not response data, just use it as-is.
			// This is so mocked requests work.
			return data;
		}
	}

	protected static getText(data: any): Promise<any> {
		let response = data;
		if (!(data instanceof Response)) {
			return response;
		}

		const contentType: string = data.headers.get('Content-Type');
		if (!contentType) {
			response = Promise.reject(
				'Failed to get Content Type from text response',
			);
		}

		if (data.ok) {
			if (contentType.includes('text/plain')) {
				response = data.text();
			} else {
				response = data as any;
			}
		} else {
			if (contentType.includes('text/plain')) {
				response = data.text().then(Promise.reject);
			} else if (data.statusText) {
				response = Promise.reject(data.statusText);
			} else {
				response = Promise.reject('Failed to process text response');
			}
		}
		return response;
	}

	protected getUrlPrefix(): string {
		return '';
	}

	/**
	 * This function will join path segments
	 */
	protected createPathWithPrefix(
		includePrefix: boolean = true,
		...path: string[]
	) {
		return (includePrefix ? this.getUrlPrefix() : '') + path.join('/');
	}

	/**
	 * This method generate the entire url and always requests the field-model
	 */
	protected generateURL(url: string, params: APIParameters = {}) {
		// ask for field-model in params
		params = {
			...params,
			'field-model': 'true',
		};
		const paramsString = GenericApi.getParamsString(params, [
			ParamType.FORM,
			ParamType.CUSTOMISATION,
			ParamType.DIVISION,
		]);

		return url + '?' + paramsString;
	}

	/**
	 * computeApiServerHostnameAndPort will compute where the API endpoints are based on the current page
	 */
	private static computeApiServerHostnameAndPort(): string {
		// port 9000 is the default developing port;
		// port 19876 is the e2e testing port, defined in protractor.conf.js
		return window.location.port === '9000' ||
			window.location.port === '19876' ||
			(window as any).Cypress
			? 'localhost:8080'
			: window.location.host;
	}

	public static getParamsString(
		finalParams: APIParameters,
		paramsToApply?: ParamType[],
		queryParams?: Map<string, string>,
	): string {
		if (!finalParams) {
			finalParams = {};
		}
		this.applyParams(finalParams, paramsToApply);
		if (queryParams && queryParams.size > 0) {
			queryParams.forEach((value: string, key: string) => {
				finalParams[key] = value;
			});
		}
		return this.encodeQueryParams(finalParams);
	}

	private static applyParams(
		existingParams: APIParameters,
		paramsToApply: ParamType[],
	): void {
		if (!paramsToApply) {
			return;
		}

		paramsToApply.forEach((param: ParamType) => {
			switch (param) {
				case ParamType.FORM:
					// NOTE: Jake: 2019-03-25 - WEBUI-1727
					// If 'form' has been passed in manually, then
					// don't override using the "GenericApi.formName" value
					// ie. customisation-editor does this.
					if (existingParams[param] === undefined) {
						existingParams[param] = GenericApi.formName;
						if (DEVELOPMENT && !existingParams.form) {
							throw new Error(
								'Developer Bug: "form" parameter is blank. This is not allowed. FYI: This error is not thrown in production code.',
							);
						}
					}
					break;
				case ParamType.CUSTOMISATION:
					if (
						GenericApi.customisationName &&
						existingParams[param] === undefined
					) {
						existingParams[param] = GenericApi.customisationName;
					}
					break;
				case ParamType.DIVISION:
					// NOTE(Jae): 2020-04-17 - WEBUI-3244
					// We now just extract division from the URL always instead of setting it
					// on GenericApi.division, which is now deleted.
					const division = GenericApi.getDivision();
					if (division) {
						existingParams[param] = division;
					}
					break;
				case ParamType.BATCH_ID:
					// Put here to ensure the exhaustiveCheck below.
					// Otherwise tslinter will complain.
					break;
				default:
					const exhaustiveCheck: never = param;
					if (DEVELOPMENT) {
						throw new Error(
							'Developer Bug: Unrecognised parameter: ' + param,
						);
					}
					break;
			}
		});
	}

	/**
	 * encodeQueryParamsJQuery is an alias of jQuery.param / $.param.
	 * We override the core jQuery.param function to make nested objects SpringBoot compatible.
	 *
	 * WARNING: Do *not* call this method. It's to be used by jQuery only.
	 */
	public static encodeQueryParamsJQuery(
		params: { [key: string]: any } | null | undefined,
	): string {
		return GenericApi.encodeQueryParams(params);
	}

	/**
	 * Replacement for jQuery.param() function. This converts an input object to a url query parameter string.
	 * eg. {a:'1', b:['2','3'],c:{d:'4',e:'5'}} maps to a=1&b%5B0%5D=2&b%5B1%5D='3'&c.d=4&c.e=5
	 *
	 * This is code was taken from jQuery 3.4.1 and modified to support Spring Boot.
	 *
	 * @param obj
	 * @returns {string} the corresponding query parameter string.
	 * @deprecated This should no longer be necessary. We have duplicated this functionality in FetchUtils.
	 */
	private static encodeQueryParams(
		params: { [key: string]: any } | null | undefined,
	): string {
		if (params === null || params === undefined) {
			return '';
		}

		const s: string[] = [];
		const add = function (key, valueOrFunction) {
			// If value is a function, invoke it and use its return value
			const value =
				typeof valueOrFunction === 'function'
					? valueOrFunction()
					: valueOrFunction;

			s.push(
				encodeURIComponent(key) +
					'=' +
					encodeURIComponent(value == null ? '' : value),
			);
		};

		// If an array was passed in, assume that it is an array of form elements.
		if (Array.isArray(params)) {
			// Serialize the form elements
			for (const name in params) {
				if (!params.hasOwnProperty(name)) {
					continue;
				}
				const value = params[name];
				add(name, value);
			}
		} else {
			// Encode params recursively.
			for (const prefix in params) {
				if (!params.hasOwnProperty(prefix)) {
					continue;
				}
				GenericApi.buildParams(prefix, params[prefix], add);
			}
		}

		// Return the resulting serialization
		return s.join('&');
	}

	private static buildParams(
		prefix: string,
		obj: { [key: string]: any },
		add: (key, value) => void,
	) {
		if (Array.isArray(obj)) {
			// Serialize array item.
			if (/\[\]$/.test(prefix)) {
				for (const v of obj) {
					add(prefix, v);
				}
			} else {
				for (let i = 0; i < obj.length; i++) {
					const v = obj[i];
					// Item is non-scalar (array or object), encode its numeric index.
					GenericApi.buildParams(
						prefix + '[' + (typeof v === 'object' ? i : '') + ']',
						v,
						add,
					);
				}
			}
		} else if (typeof obj === 'object') {
			// Serialize object item.
			for (const name in obj) {
				if (!obj.hasOwnProperty(name)) {
					continue;
				}
				// NOTE: Jake: 2019-09-05 - WEBUI-2465
				// This change is required to properly support Spring Boot.
				// See: https://stackoverflow.com/a/7203353/5013410
				GenericApi.buildParams(prefix + '.' + name, obj[name], add);
				// NOTE: Jake: 2019-09-05 - WEBUI-2465
				// This is how jQuery works by default.
				// GenericApi.buildParams( prefix + "[" + name + "]", obj[ name ], add);
			}
		} else {
			// Serialize scalar item.
			add(prefix, obj);
		}
	}

	protected async convertResponseToDownloadSpec(
		response: Response,
	): Promise<DownloadSpec> {
		const contentDisposition = response.headers.get('Content-Disposition');
		let fileName = 'default_filename.txt'; // Fallback name
		if (contentDisposition && contentDisposition.includes('filename=')) {
			const matches = /filename="([^"]+)"/.exec(contentDisposition);
			if (matches && matches[1]) {
				fileName = matches[1];
			}
		}

		const blob = await response.blob();

		return {
			url: URL.createObjectURL(blob),
			filename: fileName,
		};
	}

	/**
	 * Creates a STOMP client and connects to the specified endpoint. The client is authenticated when connecting to a
	 * destination using the provided access token.
	 *
	 * @param {string} stompEndpointUrl - The URL of the STOMP endpoint.
	 * @param {string} accessToken - The access token to authenticate the STOMP client when connecting to a destination.
	 * @param {(stompClient: Client, frame: IFrame) => void} onWebSocketError - Callback function to handle WebSocket errors.
	 * @param {(stompClient: Client, frame: IFrame) => void} onStompError - Callback function to handle STOMP errors.
	 * @param {(stompClient: Client, frame: IFrame) => void} onConnect - Callback function to handle successful connection.
	 * @returns {Client} The authenticated STOMP client.
	 */
	protected getStompClient(
		stompEndpointUrl: string,
		accessToken: string,
		onWebSocketError: (stompClient: Client, _: IFrame) => void,
		onStompError: (stompClient: Client, _: IFrame) => void,
		onConnect: (stompClient: Client, _: IFrame) => void,
	): Client {
		const stompClient = new Client({
			brokerURL: stompEndpointUrl,
			connectHeaders: { Authorization: `${accessToken}` },
			debug: (msg) => Logger.debug(msg),
		});

		stompClient.onWebSocketError = (_) => onWebSocketError(stompClient, _);
		stompClient.onStompError = (_) => onStompError(stompClient, _);
		stompClient.onConnect = (_) => onConnect(stompClient, _);

		stompClient.activate();
		return stompClient;
	}
}
